import _ from "underscore";
import React from "react";
import {styled, css} from "styled-components";

import UngroupedTestsTable from "./ungrouped_tests_table";
import FilterableName from "common_components/flowmaster/llm_run/filtering/filterable_name";

import {OrganisationId} from "common/types/organisation";
import {AggregatedWorkflowTest} from "common/flowmaster/types/test";
import {LlmRunFilterKeys} from "common/flowmaster/types/llm_run";
import {WorkflowInstanceId} from "common/flowmaster/types/workflow";

// import a show more and show less icon from material:

import ShowMore from "@material-ui/icons/ExpandMore";
import ShowLess from "@material-ui/icons/ExpandLess";

const Cell = styled.td<{
  topLevel?: boolean;
  number?: boolean;
  heading?: boolean;
  open?: boolean;
}>`
  padding: 0.25em 1em;
  text-align: ${props => (props.number ? "right" : "left")};
  font-weight: ${props => (props.heading ? "bold" : "normal")};
  ${props =>
    props.open &&
    props.topLevel &&
    css`
      border-top: 1px solid #888;
      &:first-child {
        border-left: 1px solid #888;
      }
      &:last-child {
        border-right: 1px solid #888;
      }
    `}
`;

type Props = {
  groupRevisions: Record<WorkflowInstanceId, AggregatedWorkflowTest[]>;
  organisationId: OrganisationId;
  updateValue: (
    obj: Partial<Record<LlmRunFilterKeys, string | number>>,
  ) => void;
  state: {isOpen: boolean};
  toggleOpen: () => void;
};

export default function GroupRow({
  organisationId,
  state,
  groupRevisions,
  toggleOpen,
  updateValue,
}: Props) {
  const [showPriorRevisions, setShowPriorRevisions] = React.useState(false);
  const [openRevisions, setOpenRevisions] = React.useState<
    Record<WorkflowInstanceId, boolean>
  >({});

  const latestRevisionKey = Object.entries(groupRevisions).reduce(
    (best, [configGroupId, tests]) => {
      const firstTest = tests[0];
      return best.date > firstTest.config_date
        ? best
        : {key: configGroupId, date: firstTest.config_date};
    },
    {
      key: Object.keys(groupRevisions)[0],
      date: groupRevisions[Object.keys(groupRevisions)[0]][0].config_date,
    },
  ).key;

  const revisionCount = Object.keys(groupRevisions).length;
  const latestTests = groupRevisions[latestRevisionKey];
  return (
    <>
      <SummaryRow
        organisationId={organisationId}
        toggleOpen={toggleOpen}
        state={state}
        tests={latestTests}
        revisionCount={revisionCount}
        topLevel={true}
        updateValue={updateValue}
      />
      {state?.isOpen ? (
        <tr>
          <td
            colSpan={8}
            style={{
              border: "1px solid #888",
              borderTop: "none",
              backgroundColor: "#eee",
              padding: "0.5em 0",
              paddingLeft: "2em",
            }}
          >
            <UngroupedTestsTable
              organisationId={organisationId}
              workflowTests={latestTests}
              updateValue={updateValue}
              hideCols={new Set(["Workflow", "Issue"])}
            />
            {revisionCount > 1 && (
              <div>
                <div
                  onClick={() => setShowPriorRevisions(!showPriorRevisions)}
                  style={{
                    fontStyle: "italic",
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    backgroundColor: "#fff",
                  }}
                >
                  Prior Revisions{" "}
                  {showPriorRevisions ? <ShowLess /> : <ShowMore />}
                </div>
                {showPriorRevisions && (
                  <table style={{width: "100%"}}>
                    <thead>
                      <tr style={{backgroundColor: "#ddd"}}>
                        <th style={{textAlign: "left"}}>Version</th>
                        <th style={{textAlign: "right"}}>Docs</th>
                        <th style={{textAlign: "right"}}>Avg Runs</th>
                        <th style={{textAlign: "right"}}>100% Rate</th>
                        <th style={{textAlign: "right"}}>Avg Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {_.sortBy(
                        Object.entries(groupRevisions),
                        ([, val]) => val[0].config_date,
                      )
                        .reverse()
                        .map(([configGroupId, tests]) => {
                          if (configGroupId === latestRevisionKey) {
                            return null;
                          }
                          // const firstTest = tests[0];
                          return (
                            <>
                              <SummaryRow
                                organisationId={organisationId}
                                toggleOpen={() =>
                                  setOpenRevisions({
                                    ...openRevisions,
                                    [configGroupId]: !openRevisions[
                                      configGroupId
                                    ],
                                  })
                                }
                                state={state}
                                tests={tests}
                                revisionCount={null}
                                topLevel={false}
                                updateValue={updateValue}
                              />
                              {openRevisions[configGroupId] && (
                                <tr>
                                  <td colSpan={5}>
                                    <div
                                      style={{
                                        width: "calc(100% - 3em)",
                                        marginLeft: "3em",
                                      }}
                                    >
                                      <UngroupedTestsTable
                                        organisationId={organisationId}
                                        workflowTests={tests}
                                        updateValue={updateValue}
                                        hideCols={
                                          new Set(["Workflow", "Issue"])
                                        }
                                      />
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </>
                          );
                        })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </td>
        </tr>
      ) : null}
    </>
  );
}

function SummaryRow({
  organisationId,
  toggleOpen,
  state,
  tests,
  revisionCount,
  topLevel,
  updateValue,
}) {
  const docCount = tests.length;
  const avgRunCount =
    tests.reduce((sum, test) => sum + (test.run_count ?? 0), 0) / docCount;
  const allPassRate =
    tests.reduce((sum, test) => sum + (test.average_all_pass_score ?? 0), 0) /
    docCount;
  const averageScore =
    tests.reduce((sum, test) => sum + (test.average_score ?? 0), 0) / docCount;

  return (
    <tr
      onClick={() => toggleOpen()}
      style={{
        cursor: "pointer",
        ...(state?.isOpen
          ? {
              backgroundColor: "#eee",
            }
          : {}),
      }}
    >
      {topLevel && (
        <Cell topLevel={topLevel} heading={true} open={state?.isOpen}>
          <FilterableName
            name={tests[0].workflow_name}
            url={`organisation/${organisationId}/workflow/${tests[0].workflow_id}`}
            filter={() => updateValue({workflow: tests[0].workflow_id})}
          />
        </Cell>
      )}
      {topLevel && (
        <Cell topLevel={topLevel} heading={true} open={state?.isOpen}>
          {tests[0].config_name ?? tests[0].issue_name}
        </Cell>
      )}
      <Cell topLevel={topLevel} heading={true} open={state?.isOpen}>
        <FilterableName
          name={tests[0].config_date}
          url={`organisation/${organisationId}/data-asset/${tests[0].workflow_id}/config/${tests[0].config_id}`}
          filter={() =>
            updateValue({
              workflow: tests[0].workflow_id,
              config: tests[0].config_id,
            })
          }
        />
      </Cell>
      <Cell topLevel={topLevel} number={true} open={state?.isOpen}>
        {docCount}
      </Cell>
      {topLevel && (
        <Cell topLevel={topLevel} number={true} open={state?.isOpen}>
          {revisionCount}
        </Cell>
      )}
      <Cell topLevel={topLevel} number={true} open={state?.isOpen}>
        {avgRunCount.toFixed(1)}
      </Cell>
      <Cell topLevel={topLevel} number={true} open={state?.isOpen}>
        {(allPassRate * 100).toFixed(2)}%
      </Cell>
      <Cell topLevel={topLevel} number={true} open={state?.isOpen}>
        {(averageScore * 100).toFixed(2)}%
      </Cell>
    </tr>
  );
}
